import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, Plus, Loader2, Tags, Trash2 } from "lucide-react";

export default function Labels() {
  const [labels, setLabels] = useState([]);
  const [count, setCount] = useState(12);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/labels", { params: { unlinked: true } }); setLabels(r.data); }
    catch { toast.error("Failed to load labels"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post("/labels/batch", { count: Number(count) || 12 }); toast.success(`Generated ${r.data.created} labels`); load(); }
    catch { toast.error("Failed to generate"); }
    finally { setBusy(false); }
  };

  const clearAll = async () => {
    if (!window.confirm(`Clear all ${labels.length} unlinked QR labels? Any printed sheets with these codes will no longer scan.`)) return;
    setBusy(true);
    try { const r = await api.delete("/labels/unlinked"); toast.success(`Cleared ${r.data.deleted} labels`); load(); }
    catch { toast.error("Failed to clear labels"); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 no-print">
        <div>
          <p className="mono text-xs uppercase tracking-[0.2em] text-brand mb-2">Mail-in Intake</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Blank QR Labels</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">Print a sheet of pre-numbered QR stickers. Stick one on each incoming envelope the moment film arrives, then scan it at Intake to link it to the matching order — so nothing sits anonymous on the bench.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Quantity</label>
            <Input type="number" value={count} onChange={(e) => setCount(e.target.value)} min={1} max={100} className="w-24 mt-1.5" data-testid="label-count-input" />
          </div>
          <Button onClick={generate} disabled={busy} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="generate-labels-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}<span className="ml-1.5">Generate</span>
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-labels-btn"><Printer className="w-4 h-4 mr-1.5" /> Print sheet</Button>
          {labels.length > 0 && (
            <Button variant="outline" onClick={clearAll} disabled={busy} className="text-ruby border-ruby/40 hover:bg-ruby/10" data-testid="clear-labels-btn">
              <Trash2 className="w-4 h-4 mr-1.5" /> Clear all
            </Button>
          )}
        </div>
      </div>

      {labels.length === 0 ? (
        <div className="py-24 text-center text-muted-foreground no-print">
          <Tags className="w-8 h-8 mx-auto mb-3 opacity-50" />
          No unlinked labels. Generate a batch to print.
        </div>
      ) : (
        <div className="print-area grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="labels-grid">
          {labels.map((l) => (
            <div key={l.id} className="bg-white rounded-lg p-4 flex flex-col items-center text-center border border-border print-label" data-testid={`label-card-${l.code}`}>
              <QRCodeSVG value={l.token} size={120} level="M" />
              <div className="mono text-black font-bold text-sm mt-2">{l.code}</div>
              <div className="mono text-black/50 text-[9px] mt-0.5 uppercase tracking-wider">Low Five Film Lab · Intake</div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
